import React, { useId } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { NAV_MODE_LISTENER, NAV_MODE_TALKER } from '../../util/siteNavMode';

import css from './ModeSwitchToggle.module.css';

/**
 * Dual-role Listener | Talker slide toggle. Active side is filled
 * (Listening blue or Talking pink) and the thumb slides on tap.
 * Single-role accounts do not render this control.
 *
 * @param {Object} props
 * @param {'listener'|'talker'} props.mode
 * @param {Function} props.onChange
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const ModeSwitchToggle = props => {
  const { mode, onChange, className } = props;
  const titleId = useId();
  const isTalker = mode === NAV_MODE_TALKER;
  const current = isTalker ? NAV_MODE_TALKER : NAV_MODE_LISTENER;

  const select = next => {
    if (typeof onChange !== 'function' || next === current) {
      return;
    }
    onChange(next);
  };

  const onKeyDown = event => {
    if (event.key === 'ArrowLeft' || event.key === 'Home') {
      event.preventDefault();
      select(NAV_MODE_LISTENER);
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'End') {
      event.preventDefault();
      select(NAV_MODE_TALKER);
    }
  };

  return (
    <div className={classNames(css.wrap, className)} data-position="static">
      <p id={titleId} className={css.title}>
        <FormattedMessage id="ModeSwitchToggle.label" />
      </p>
      <div
        className={classNames(css.root, isTalker ? css.talker : css.listener)}
        role="radiogroup"
        aria-labelledby={titleId}
        data-mode-switch={current}
        data-dashboard-switch={isTalker ? NAV_MODE_LISTENER : NAV_MODE_TALKER}
        onKeyDown={onKeyDown}
      >
        <span className={css.thumb} aria-hidden="true" />
        <button
          type="button"
          role="radio"
          aria-checked={!isTalker}
          className={classNames(css.option, !isTalker && css.optionOn)}
          onClick={() => select(NAV_MODE_LISTENER)}
        >
          <FormattedMessage id="ModeSwitchToggle.listener" />
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={isTalker}
          className={classNames(css.option, isTalker && css.optionOn)}
          onClick={() => select(NAV_MODE_TALKER)}
        >
          <FormattedMessage id="ModeSwitchToggle.talker" />
        </button>
      </div>
    </div>
  );
};

export default ModeSwitchToggle;
